import type React from 'react';

/**
 * One colour per disposition — the only place the frontend defines them.
 *
 * Mirrors DISPOSITION_CATALOG in contacts-nest
 * (`src/metrics/canonical/disposition-catalog.ts`), which explains how the
 * palette was generated and what it replaced. The overview's pie chart already
 * receives these hex values straight from the API; the calls table and the call
 * path read them from here, so THE SAME DISPOSITION IS THE SAME COLOUR on all
 * three surfaces.
 *
 * Before this, the table kept its own Tailwind map where four dispositions were
 * grey, three were red and two were cyan — and none of them matched the chart.
 *
 * Hex rather than Tailwind classes on purpose: the chart colours arrive from
 * the API as hex, and no utility class can be guaranteed to equal an arbitrary
 * one. Matching exactly is the whole point.
 */
const DISPOSITION_HEX: Record<string, string> = {
  set_appointment: '#298c01',
  decision_maker_conversation: '#00ae67',
  gatekeeper_conversation: '#9a6e0b',
  phone_refusal: '#b63590',
  opt_out: '#d03739',
  referred_another_contact: '#19a9ae',
  no_longer_with_company: '#834ecf',
  transferred_without_disposition: '#089be8',
  refuse_transfer: '#ed5790',
  other: '#91918a',
  left_vm: '#0072dd',
  no_answer: '#7a81f8',
  wrong_number: '#e37100',
  phone_tree_no_connection: '#c767dc',
  bad_number_busy_signal: '#8f9a00',
  sdr_did_not_pick_up: '#008872',
  default_action: '#6e6e68',
};

/** SDR-facing codes resolve to the same colour as their long form. */
const ABBREVIATION_TO_VALUE: Record<string, string> = {
  'SA': 'set_appointment',
  'DMC': 'decision_maker_conversation',
  'GC': 'gatekeeper_conversation',
  'PR': 'phone_refusal',
  'OO': 'opt_out',
  'RAC': 'referred_another_contact',
  'NLC': 'no_longer_with_company',
  'TND': 'transferred_without_disposition',
  'RTT': 'refuse_transfer',
  'OTR': 'other',
  'LVM': 'left_vm',
  'NA': 'no_answer',
  'WN': 'wrong_number',
  'PTNC': 'phone_tree_no_connection',
  'BN/BS': 'bad_number_busy_signal',
  'SDNPU': 'sdr_did_not_pick_up',
  'DA': 'default_action',
};

/**
 * Not a stored disposition: the frontend shows it for a dial that never
 * connected (see `resolveDisposition` in CallsTable). Deliberately the dimmest
 * thing on the row — nothing happened, so it should not compete with the calls
 * that produced an outcome.
 */
export const NOT_CONNECTED = 'not_connected';

const FALLBACK = '#91918a';

/** The canonical colour for a disposition, given either form or an unknown value. */
export function dispositionHex (value?: string | null): string {
  if (value == null || value === '') {
    return FALLBACK;
  }

  const canonical = ABBREVIATION_TO_VALUE[value] ?? value;

  return DISPOSITION_HEX[canonical] ?? FALLBACK;
}

/**
 * Badge styling for a disposition: the colour itself as text and border, over a
 * wash of the same hue.
 *
 * The alpha suffixes keep the badge legible on both surfaces — a 14% fill is a
 * tint on white and a glow on near-black, while the text stays the full-strength
 * hue that the chart legend shows.
 */
export function dispositionBadgeStyle (value?: string | null): React.CSSProperties {
  if (value === NOT_CONNECTED) {
    return {
      color: 'var(--muted-foreground)',
      borderColor: 'color-mix(in srgb, var(--muted-foreground) 40%, transparent)',
      borderStyle: 'dashed',
      backgroundColor: 'transparent',
    };
  }

  const hex = dispositionHex(value);

  return {
    color: hex,
    borderColor: `${hex}66`,
    backgroundColor: `${hex}24`,
  };
}
